/**
 * Executive Report view helpers (blueprint §5.2 reports, §7 C9 and C28).
 * Pure, so the rules are unit-tested apart from React.
 *
 * Known-value rule (blueprint "§5.2 wiring note"): a component the report
 * has no value for is `null`, never a placeholder 0, so the waffle, the
 * component tiles and the brief read the same numbers. The Guardian Score
 * weights and the waffle maths come from the Command Center helpers
 * (cc-score.ts), which mirror ExecutiveReportService.CalculateGuardianScore
 * (Secure Score 30, Best Practice 30, MFA 20, baseline 20).
 */
import type { Category, GridColumnCount, WaffleSegment } from '@/components/carbon'
import { GUARDIAN_WEIGHTS, signed, waffleSegments, type GuardianComponents } from '@/components/Dashboard/v2/cc-score'
import { formatAssessedClock } from '@/components/common/LastAssessed'
import type { ExecutiveReportData, FrameworkScoreSummary, ScoreDataPoint } from '@/types/executiveReport'

const finite = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

/* -------------------------------------------------------------------------- */
/* Components and deltas                                                      */
/* -------------------------------------------------------------------------- */

/** The report's four Guardian Score components, 0..100 each, or null when the report has no value. */
export function reportComponents(report: ExecutiveReportData): GuardianComponents {
  return {
    baseline: finite(report.baselineCompliance?.current),
    bpa: finite(report.bpaScore?.current),
    identity: finite(report.mfaAdoption?.current),
    secure: finite(report.secureScore?.current),
  }
}

/** The Guardian Score waffle's four groups for this report (largestRemainder via cc-score). */
export function reportSegments(report: ExecutiveReportData): WaffleSegment[] {
  return waffleSegments(reportComponents(report))
}

/**
 * A delta tag over the reporting period, "+4 in 30 days" or "No change in
 * 30 days" (§7 C9: the period's own days, never "weeks"). Null when the
 * change or the period is unknown.
 */
export function deltaInDays(change: number | null | undefined, days: number | null | undefined): string | null {
  const c = finite(change)
  const d = finite(days)
  if (c == null || d == null || d <= 0) return null
  if (Math.round(c * 10) / 10 === 0) return `No change in ${d} days`
  return `${signed(c)} in ${d} days`
}

/** The Guardian Score tile's delta: current against the period-start score, null when either is missing. */
export function guardianScoreDelta(report: ExecutiveReportData, days: number): string | null {
  const now = finite(report.currentGuardianScore)
  const start = finite(report.periodStartGuardianScore)
  return now == null || start == null ? null : deltaInDays(now - start, days)
}

/* -------------------------------------------------------------------------- */
/* Component tiles (mockup 01 pillar row)                                     */
/* -------------------------------------------------------------------------- */

export interface ComponentTileSpec {
  category: Category
  /** The pillar's name, as the Command Center tiles name it. */
  title: string
  to: string
  linkLabel: string
  /** One sentence saying what the score measures. */
  definition: string
  /** 0..100, or null when the report has no value. */
  score: number | null
  /** "+4 in 30 days", or null. */
  delta: string | null
  /** "17 of 20 points toward your Guardian Score", or null when the component has no value. */
  caption: string | null
}

const TILE: Record<Category, Pick<ComponentTileSpec, 'title' | 'to' | 'linkLabel' | 'definition'>> = {
  baseline: {
    title: 'Baseline Compliance',
    to: '/configuration-drift',
    linkLabel: 'Open Baseline Compliance',
    definition: 'The share of checked resources that still match your baseline.',
  },
  bpa: {
    title: 'Best Practice',
    to: '/security?tab=best-practices',
    linkLabel: 'Open Security Center',
    definition: 'The severity-weighted pass rate across your starred frameworks.',
  },
  identity: {
    title: 'Identity',
    to: '/user-insights',
    linkLabel: 'Open User Analytics',
    definition: 'The share of members who sign in with MFA.',
  },
  secure: {
    title: 'Secure Score',
    to: '/security?tab=secure-score',
    linkLabel: 'Open Secure Score',
    definition: 'Read from Microsoft for your tenant.',
  },
}

const CHANGE: Record<Category, keyof Pick<ExecutiveReportData, 'baselineCompliance' | 'bpaScore' | 'mfaAdoption' | 'secureScore'>> = {
  baseline: 'baselineCompliance',
  bpa: 'bpaScore',
  identity: 'mfaAdoption',
  secure: 'secureScore',
}

/** The four component tiles, in waffle order, with each one's points toward the Guardian Score. */
export function componentTiles(report: ExecutiveReportData, days: number): ComponentTileSpec[] {
  const components = reportComponents(report)
  return reportSegments(report).map((seg) => ({
    category: seg.category,
    ...TILE[seg.category],
    score: components[seg.category],
    delta: deltaInDays(report[CHANGE[seg.category]]?.change, days),
    caption: seg.earned == null ? null : `${seg.earned} of ${GUARDIAN_WEIGHTS[seg.category]} points toward your Guardian Score`,
  }))
}

/* -------------------------------------------------------------------------- */
/* Trend                                                                      */
/* -------------------------------------------------------------------------- */

const TREND_FIELD: Record<Category, 'baselineCompliance' | 'bpaScore' | 'mfaAdoption' | 'secureScore'> = {
  baseline: 'baselineCompliance',
  bpa: 'bpaScore',
  identity: 'mfaAdoption',
  secure: 'secureScore',
}

/** "2026-10-05" from an ISO date or date-time: the UTC calendar day, as the server buckets the trend. */
function dayKey(iso: unknown): string | null {
  const m = typeof iso === 'string' ? /^(\d{4}-\d{2}-\d{2})/.exec(iso) : null
  return m ? m[1] : null
}

/**
 * The trend rows the chart plots, ending on the tiles' values.
 *
 * The two read different fields. The tiles and the Guardian Score use each
 * component's live value at compile time (`ScoreWithTrend.current`), while
 * `guardianScoreTrend` is the daily snapshot history (ExecutiveReportService
 * BuildTrendDataPoints). A snapshot taken earlier in the day can sit a point
 * off the live value, which left a line's end label disagreeing with its tile.
 * The report's own day (`generatedAt`) therefore takes the live value of every
 * component the report knows, replacing that day's snapshot or appending the
 * day when the snapshots stop short of it. An unknown live value keeps the
 * snapshot, and a report with no history gets no single synthetic point.
 *
 * Best Practice's history is the server's overall score row (every scored
 * framework), not the starred frameworks the live value averages, so its
 * last segment carries that difference, as the tile's period delta already
 * does, until the server scores both on one basis.
 */
export function reportTrendRows(report: ExecutiveReportData): ScoreDataPoint[] {
  const rows = report.guardianScoreTrend ?? []
  const day = dayKey(report.generatedAt)
  const live = reportComponents(report)
  const known = (Object.keys(TREND_FIELD) as Category[]).filter((k) => live[k] != null)
  if (!day || rows.length === 0 || known.length === 0) return rows

  const at = rows.findIndex((r) => dayKey(r.date) === day)
  const newest = rows.reduce<string | null>((max, r) => {
    const k = dayKey(r.date)
    return k != null && (max == null || k > max) ? k : max
  }, null)
  // A snapshot newer than the report's own day would mean mismatched clocks; leave the history as the server sent it.
  if (at < 0 && newest != null && newest > day) return rows

  const base: ScoreDataPoint =
    at >= 0
      ? rows[at]
      : { date: `${day}T00:00:00Z`, guardianScore: null, secureScore: null, bpaScore: null, mfaAdoption: null, baselineCompliance: null }
  const end: ScoreDataPoint = { ...base, guardianScore: finite(report.currentGuardianScore) ?? base.guardianScore }
  for (const k of known) end[TREND_FIELD[k]] = live[k]

  return at >= 0 ? rows.map((r, i) => (i === at ? end : r)) : [...rows, end]
}

/* -------------------------------------------------------------------------- */
/* Frameworks                                                                 */
/* -------------------------------------------------------------------------- */

export type FrameworkBand = 'strong' | 'soft' | 'weak'

/** The executive bands: 80 and above strong, 60 to 79 soft, below 60 weak. */
export function frameworkBand(score: number): FrameworkBand {
  if (score >= 80) return 'strong'
  if (score >= 60) return 'soft'
  return 'weak'
}

const BAND_RANK: Record<FrameworkBand, number> = { weak: 0, soft: 1, strong: 2 }

/** Weakest band first, then the lower score, so the tile that needs the most work leads. */
export function orderFrameworks(frameworks: ReadonlyArray<FrameworkScoreSummary>): FrameworkScoreSummary[] {
  return [...frameworks].sort((a, b) => {
    const r = BAND_RANK[frameworkBand(a.score)] - BAND_RANK[frameworkBand(b.score)]
    return r !== 0 ? r : a.score - b.score
  })
}

/**
 * One sentence per framework, keyed to its band. Only the single lowest
 * framework (`isLowest`, the first after ordering) earns "needs the most
 * work"; every other weak one states its score plainly.
 */
export function frameworkVerdict(name: string, score: number, isLowest: boolean): string {
  const rounded = Math.round(score)
  switch (frameworkBand(score)) {
    case 'strong':
      return `${name} scores ${rounded}, above 80.`
    case 'soft':
      return `${name} scores ${rounded}. Close the gap to 80.`
    case 'weak':
      return isLowest ? `${name} scores ${rounded} and needs the most work.` : `${name} scores ${rounded}, below 60.`
  }
}

// Literal class names so Tailwind's content scan finds every variant.
const MD_SPAN: Record<number, string> = { 1: '', 2: 'c-md:col-span-2' }
const XLG_SPAN: Record<number, string> = { 1: 'c-xlg:col-span-1', 2: 'c-xlg:col-span-2', 3: 'c-xlg:col-span-3', 4: 'c-xlg:col-span-4' }

export interface FrameworkGrid {
  columns: { base: GridColumnCount; 'c-md'?: GridColumnCount; 'c-xlg'?: GridColumnCount }
  /** Classes for the last tile, so it spans what is left of a short last row instead of leaving an empty gutter cell. */
  lastClassName: string
}

/**
 * The condensed grid for n framework tiles: two across from 672px, and four
 * (or three, when that leaves fewer gaps) from 1312px. A short last row is
 * filled by widening its last tile; the grid never shows an empty cell.
 */
export function frameworkGrid(n: number): FrameworkGrid {
  if (n <= 1) return { columns: { base: 1 }, lastClassName: '' }
  const md: GridColumnCount = 2
  const xlg: GridColumnCount = n <= 4 ? (n as GridColumnCount) : n === 5 || n % 3 === 0 ? 3 : 4
  const span = (cols: number) => {
    const r = n % cols
    return r === 0 ? 1 : cols - r + 1
  }
  const mdSpan = span(md)
  const xlgSpan = span(xlg)
  // The c-xlg class resets a c-md span whenever c-md set one, so a wide phone-row tile is not carried up.
  const lastClassName = [MD_SPAN[mdSpan], mdSpan > 1 || xlgSpan > 1 ? XLG_SPAN[xlgSpan] : ''].filter(Boolean).join(' ')
  return { columns: { base: 1, 'c-md': md, 'c-xlg': xlg }, lastClassName }
}

/* -------------------------------------------------------------------------- */
/* Provenance                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The brief's provenance line (DESIGN.md, D36): "Compiled by Guardian at
 * 06:12 today · <tenant name> · 30-day period". The report is compiled
 * on request from Guardian's assessments, so the verb is "Compiled", and the
 * time is the report's own `generatedAt`.
 */
export function reportProvenance(input: {
  generatedAt: string | null | undefined
  tenantName?: string | null
  days: number
  now?: Date
}): string {
  const clock = input.generatedAt ? formatAssessedClock(input.generatedAt, input.now) : null
  const lead = clock ? `Compiled by Guardian ${clock}` : 'Compiled by Guardian'
  return [lead, input.tenantName?.trim() || null, `${input.days}-day period`].filter(Boolean).join(' · ')
}
